import { CampaignBatchPayload } from "@/types/BatchPayload.interface"
import Campaign from "@/types/Campaign.interface"

interface State {
    campaignCount: number
    campaigns: Campaign[]
}

const defaults = () => {
    return {
        campaignCount: 0,
        campaigns: []
    }
}

export default {
    state: defaults(),
    mutations: {
        campaignCountSet (state: State, count: number): void {
            state.campaignCount = count
        },
        campaignsPush (state: State, campaigns: Campaign[]): void {
            //  Update our campaigns dataset
            state.campaigns = window.removeDuplicatesById(state.campaigns, campaigns)

            //  Update our count
            state.campaignCount = state.campaigns.length
        },
        campaignsRemove (state: State, ids: string[]): void {
            //  Get our global list of campaigns
            const campaigns = state.campaigns

            //  If no campaigns, do nothing
            if (!campaigns) return

            //  Else, filter out our removed ids
            const filtered = campaigns.filter((x: Campaign) => !ids.includes(x.id))

            //  Replace our list with the filtered list
            state.campaigns = filtered

            //  Update our count
            state.campaignCount = state.campaigns.length
        },
        campaignsReset(state: State): void {
            Object.assign(state, defaults())
        }
    },
    actions: {
        campaignsDelete({ commit, getters }, campaignId: string): Promise<Campaign> {
            //  Assemble the API endpoint
            const url = `${getters.api}/campaigns/${campaignId}`

            //  Send the request
            return window.axios.delete(url).then(response => {
                //  Remove the campaign from the global list
                commit('campaignsRemove', [response.data.id])

                //  Remove this campaign from the autoresponders list
                commit('autorespondersCampaignsRemove', [response.data.id])

                //  Return the now deleted autoresponder campaign
                return response.data
            }).catch(() => null)
        },
        campaignsDeleteBatch({ commit, getters }, ids: string[]): Promise<number> {
            //  Construct our payload (delete rqeuests require this if we want to send a body)
            const payload = { data: { campaign_ids: ids} }

            //  Assemble the API endpoint
            const url = `${getters.api}/campaigns/batch`

            //  Send the request
            return window.axios.delete(url, payload).then(response => {
                //  Remove the campaign from the global list
                commit('campaignsRemove', response.data)

                //  Remove the campaigns from the autoresponders list
                commit('autorespondersCampaignsRemove', response.data)

                //  Return the ids of our deleted groups
                return response.data
            }).catch(() => null)
        },
        campaignsFind({ commit, getters }, campaignId: string): Promise<Campaign> {
            //  Assemble the API endpoint
            const url = `${getters.api}/campaigns/${campaignId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Add the campaign to our global dataset
                commit('campaignsPush', [response.data])
                
                //  Return our campaign we found
                return response.data
            }).catch(() => null)
        },
        campaignsPost({ commit, getters }, campaign: Campaign): Promise<Campaign> {
            //  Assemble the API endpoint
            const url = `${getters.api}/campaigns`

            //  Send the request
            return window.axios.post(url, campaign).then(response => {
                //  Add the new campaign to our global dataset
                commit('campaignsPush', [response.data])

                //  Add the campaign to our autoresponders dataset
                commit('autorespondersCampaignsSync', [response.data])

                //  Return the newly created autoresponder campaign
                return response.data
            }).catch(() => null)
        },
        campaignsPut({ commit, getters }, campaign: Campaign): Promise<Campaign> {
            //  Assemble the API endpoint
            const url = `${getters.api}/campaigns`

            //  Send the request
            return window.axios.put(url, campaign).then(response => {
                //  Update our campaigns list
                commit('campaignsPush', [response.data])

                //  Update the campaign in our autoresponders dataset
                commit('autorespondersCampaignsSync', [response.data])

                //  Return the newly updated campaign
                return response.data
            }).catch(() => null)
        },
        campaignsPutBatch({ commit, getters }, campaigns: CampaignBatchPayload): Promise<Campaign[]> {
            //  Assemble the API endpoint
            const url = `${getters.api}/campaigns/batch`

            //  Send the request
            return window.axios.put(url, campaigns).then(response => {
                //  Update our campaigns list
                commit('campaignsPush', response.data)

                //  Update the campaign in our autoresponders dataset
                commit('autorespondersCampaignsSync', response.data)

                //  Return the updated campaigns
                return response.data
            }).catch(e => null)
        }
    },
    getters: {
        campaignCount (state: State): number {
            return state.campaignCount
        },
        campaigns (state: State): Campaign[] {
            return state.campaigns
        }
    }
}
